import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Reveal, MaskLine, SectionLabel } from "@/components/Reveal";

const employerStages = [
  { n: "01", title: "Understand", sub: "Your requirement comes first", text: "We take the time to understand your requirement, business context, team, and the talent you need." },
  { n: "02", title: "Source", sub: "Targeted, not scattered", text: "We identify relevant talent through targeted sourcing, market research, and our candidate network." },
  { n: "03", title: "Screen", sub: "Structure over volume", text: "We evaluate candidates against the requirements of the role, including experience, skills, expectations, and availability." },
  { n: "04", title: "Shortlist", sub: "Focused selection", text: "We present a focused selection of relevant candidates rather than an unnecessary stack of profiles." },
  { n: "05", title: "Coordinate", sub: "Momentum, managed", text: "We support interview scheduling, communication, feedback, and follow-ups throughout the hiring process." },
  { n: "06", title: "Close & Support", sub: "Beyond the offer", text: "We remain involved through the offer and joining stage to help create a smooth experience." },
];

const candidateStages = [
  { n: "01", title: "Understand Your Goals", sub: "You come first", text: "We take the time to understand your experience, strengths, and the kind of opportunity you're looking for." },
  { n: "02", title: "Identify the Right Fit", sub: "Relevant, not random", text: "We connect your profile with opportunities that are relevant to your experience and aspirations." },
  { n: "03", title: "Discuss the Opportunity", sub: "Full picture, always", text: "We help you understand the role, organization, expectations, and overall opportunity." },
  { n: "04", title: "Prepare & Connect", sub: "Guided throughout", text: "We guide you through the recruitment process and coordinate with the relevant stakeholders." },
  { n: "05", title: "Stay Connected", sub: "Clear communication", text: "We keep communication clear throughout the process and help you navigate the next steps." },
  { n: "06", title: "Support Your Move", sub: "Through the transition", text: "We remain connected through the offer and joining process to help make the transition smoother." },
];

const expectations = [
  { title: "Clarity", text: "You always know where things stand — what's happening, what's next, and what we need from you." },
  { title: "Relevant Talent", text: "Profiles are shared because they fit the requirement — not to fill your inbox." },
  { title: "Human Judgement", text: "Every recommendation is shaped by people who understand both the role and the person behind the resume." },
  { title: "Structured Execution", text: "A defined approach, consistent follow-through, and a process that respects everyone's time." },
  { title: "One Partner, Multiple Solutions", text: "Recruitment, staffing, RPO and payroll support — from one team that understands your business." },
];

const beliefs = [
  "We don't believe in sending profiles simply to increase numbers.",
  "We don't believe every hiring requirement needs the same solution.",
  "We don't believe communication should disappear after profiles are shared.",
  "And we don't believe recruitment should feel complicated for the people involved.",
];

const faqs = [
  { q: "How do we start working with Genspire?", a: "It starts with your requirement. Share the details through our requirement form, and our team will review what you've shared and get in touch to understand it properly before suggesting a way forward." },
  { q: "Do you support both one-time hiring and ongoing requirements?", a: "Yes. Whether you're hiring for a single critical role, building out a team, or looking for continuous recruitment or payroll support, the engagement is structured around what you actually need." },
  { q: "Which roles and functions do you cover?", a: "We support professional roles across functions such as software and technology, engineering, sales and business development, finance and accounting, human resources, and operations — along with other professional roles." },
  { q: "What happens after I share my requirement?", a: "Our team reviews the information, connects with you to understand the requirement in more depth, and aligns with you on the right approach. From there, we stay involved through the relevant stages of the engagement." },
];

export default function HowGenspireWorks() {
  const [openFaq, setOpenFaq] = useState(0);
  const [processTab, setProcessTab] = useState("employers");

  return (
    <>
      <section className="pt-[124px] pb-12 lg:pt-[150px] lg:pb-16 relative overflow-hidden" data-testid="hgw-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full grid grid-cols-2 lg:grid-cols-4">
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60" />
            <div className="border-l border-slate-200/60 hidden lg:block" />
            <div className="border-l border-r border-slate-200/60 hidden lg:block" />
          </div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MaskLine delay={0.05}>
            <span className="flex items-center gap-4">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-[11px] uppercase tracking-[0.38em] text-brand-red font-medium">How Genspire Works</span>
            </span>
          </MaskLine>
          <h1 className="mt-7 font-display font-extrabold tracking-tight text-slate-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl">
            <MaskLine delay={0.18}>A clearer approach to connecting</MaskLine>
            <MaskLine delay={0.3}>people and <span className="text-brand-red">opportunities.</span></MaskLine>
          </h1>
          <MaskLine delay={0.44} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
              Every hiring requirement and every career move is different. Our approach is built around understanding the people, requirements, expectations, and context behind each connection.
            </p>
          </MaskLine>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-brand-light/60" data-testid="hgw-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>The Process</SectionLabel>
            <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl mb-8">Six Stages. One Continuous Journey.</h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="inline-flex border border-slate-200 mb-12" data-testid="hgw-process-tabs">
              {[["employers", "For Employers"], ["candidates", "For Candidates"]].map(([key, label]) => (
                <button key={key} onClick={() => setProcessTab(key)} data-testid={`hgw-tab-${key}`}
                  className={`px-7 py-3.5 text-sm font-medium transition-colors duration-300 ${processTab === key ? "bg-brand-red text-white" : "bg-white text-slate-600 hover:text-brand-red"}`}>
                  {label}
                </button>
              ))}
            </div>
          </Reveal>
          <div className="relative">
            <span aria-hidden className="absolute left-[19px] top-2 bottom-2 w-px bg-slate-200 lg:left-0 lg:top-[19px] lg:bottom-auto lg:h-px lg:w-full lg:bg-gradient-to-r lg:from-brand-red/40 lg:via-slate-200 lg:to-slate-200" />
            <div className="grid gap-10 lg:grid-cols-6 lg:gap-6">
              {(processTab === "employers" ? employerStages : candidateStages).map((s, i) => (
                <Reveal key={`${processTab}-${s.n}`} delay={i * 0.07}>
                  <div className="relative pl-14 lg:pl-0 lg:pt-14" data-testid={`hgw-stage-${processTab}-${s.n}`}>
                    <span className="absolute left-0 top-0 lg:left-0 lg:top-0 flex h-10 w-10 items-center justify-center rounded-full bg-white border-2 border-brand-red font-mono text-[11px] font-semibold text-brand-red">
                      {s.n}
                    </span>
                    <h3 className="font-display font-semibold text-slate-900 text-lg">{s.title}</h3>
                    <p className="mt-1 text-[13px] font-medium text-brand-red">{s.sub}</p>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.15}>
            <Link to={processTab === "employers" ? "/discuss-your-requirement" : "/careers/openings"}
              data-testid="hgw-process-cta"
              className="group mt-12 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-dark text-white font-medium text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5">
              {processTab === "employers" ? "Start a Hiring Conversation" : "Explore Opportunities"}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-brand-ink text-white" data-testid="hgw-expectations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel light>What You Can Expect</SectionLabel>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-4xl">What You Can Expect From <span className="text-brand-red">Genspire</span></h2>
          </Reveal>
          <div className="mt-12 border-t border-white/10">
            {expectations.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.05}>
                <div className="grid sm:grid-cols-12 gap-3 sm:gap-8 items-start py-7 border-b border-white/10" data-testid={`hgw-expectation-${i + 1}`}>
                  <span className="sm:col-span-1 font-mono text-sm text-brand-red font-medium pt-1">0{i + 1}</span>
                  <h3 className="sm:col-span-4 font-display font-semibold text-lg">{e.title}</h3>
                  <p className="sm:col-span-7 text-sm sm:text-base text-white/60 leading-relaxed">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20" data-testid="hgw-beliefs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>What We Believe In</SectionLabel>
          </Reveal>
          <div className="space-y-7">
            {beliefs.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p className="font-display font-semibold text-slate-900 text-xl sm:text-3xl leading-snug" data-testid={`hgw-belief-${i + 1}`}>
                  {b.split("don't believe").length > 1 ? (
                    <>We <span className="text-brand-red">don't believe</span>{b.split("don't believe")[1]}</>
                  ) : b}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 border-l-2 border-brand-red pl-7" data-testid="hgw-beliefs-closing">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                Our role is to make the process more focused, transparent and easier to navigate.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-brand-light/60" data-testid="hgw-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="font-display font-bold tracking-tight text-slate-900 text-2xl sm:text-4xl mb-10">Common Questions</h2>
          </Reveal>
          <div className="border-t border-slate-200">
            {faqs.map((f, i) => (
              <div key={i} className="border-b border-slate-200" data-testid={`hgw-faq-${i + 1}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} data-testid={`hgw-faq-toggle-${i + 1}`}
                  className="w-full flex items-center justify-between gap-6 py-5 text-left group">
                  <span className={`text-sm sm:text-base font-medium transition-colors ${openFaq === i ? "text-brand-red" : "text-slate-800 group-hover:text-brand-red"}`}>{f.q}</span>
                  <ChevronDown size={16} className={`shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180 text-brand-red" : "text-slate-400"}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-400 ${openFaq === i ? "max-h-48 pb-5" : "max-h-0"}`}>
                  <p className="text-sm text-slate-600 leading-relaxed pr-8">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/discuss-your-requirement" data-testid="hgw-faq-cta"
              className="group mt-8 inline-flex items-center gap-3 text-sm font-medium text-brand-red">
              Still Have Questions? Let's Talk
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-brand-red" data-testid="hgw-closing-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-bold tracking-tight text-white text-2xl sm:text-4xl lg:text-5xl leading-[1.12]">
              Let's Start With Your Requirement.
            </h2>
            <p className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
              Tell us what you're looking for and let's understand how Genspire can support you.
            </p>
            <Link to="/discuss-your-requirement" data-testid="hgw-closing-button"
              className="group mt-9 inline-flex items-center gap-2 bg-white text-brand-red hover:bg-brand-ink hover:text-white font-medium text-sm px-9 py-4 transition-all duration-300 hover:-translate-y-0.5">
              Discuss Your Requirement
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
